import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CustomFieldTypeName } from '@air/api/types';

import { CustomFieldFilter } from '~/components/Filters/CustomFieldFilter/CustomFieldFilter';
import { DateCustomFieldFilterControls } from '~/components/Filters/CustomFieldFilter/DateCustomFieldFilterControls';
import { PlainTextCustomFieldFilterControls } from '~/components/Filters/CustomFieldFilter/PlainTextCustomFieldFilterControls';
import { type CustomFieldValueFilterBase } from '~/components/Filters/CustomFieldFilter/utils';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import { type CustomFieldFilterType } from '~/components/FiltersMenu/types';
import {
  clearCustomFieldFilterValuesAction,
  setCustomFieldFilterLogicAction,
  setCustomFieldFilterValuesAction,
} from '~/store/filters/actions';
import {
  customFieldFilterLogicSelector,
  customFieldFilterValuesSelector,
  hasCustomFieldFilterSelectedSelector,
} from '~/store/filters/selectors';
import { type FilterLogicType } from '~/store/filters/types';
import { useGetPrivateCustomFieldOptions } from '~/swr-hooks/customFields/useGetPrivateCustomFieldOptions';
import { selectableCustomFields } from '~/swr-hooks/customFields/utils';
import { useAirSelector } from '~/utils/ReduxUtils';

export type SingleCustomFieldFilterProps = {
  customField: CustomFieldFilterType;
} & SingleFilterProps;

const SinglePlainTextCustomFieldFilter = memo(({ customField, onApplyClick }: SingleCustomFieldFilterProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector((store) => hasCustomFieldFilterSelectedSelector(store, customField.id));

  const clearValues = useCallback(() => {
    dispatch(clearCustomFieldFilterValuesAction({ customFieldId: customField.id }));
  }, [customField.id, dispatch]);

  return (
    <SingleFilterContainer
      currentCustomField={customField}
      currentFilter="customField"
      hasValues={hasValues}
      onApplyClick={onApplyClick}
      onClearClick={clearValues}
    >
      <PlainTextCustomFieldFilterControls
        customFieldId={customField.id}
        fieldName={customField.name}
        onCommitted={onApplyClick}
      />
    </SingleFilterContainer>
  );
});

SinglePlainTextCustomFieldFilter.displayName = 'SinglePlainTextCustomFieldFilter';

const SingleDateCustomFieldFilter = memo(({ customField, onApplyClick }: SingleCustomFieldFilterProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector((store) => hasCustomFieldFilterSelectedSelector(store, customField.id));

  const handleClearValues = useCallback(() => {
    dispatch(clearCustomFieldFilterValuesAction({ customFieldId: customField.id }));
  }, [customField.id, dispatch]);

  return (
    <SingleFilterContainer
      currentCustomField={customField}
      currentFilter="customField"
      hasValues={hasValues}
      onApplyClick={onApplyClick}
      onClearClick={handleClearValues}
    >
      <DateCustomFieldFilterControls
        customFieldId={customField.id}
        fieldName={customField.name}
        onCommitted={onApplyClick}
      />
    </SingleFilterContainer>
  );
});

SingleDateCustomFieldFilter.displayName = 'SingleDateCustomFieldFilter';

const SingleSelectCustomFieldFilter = memo(({ customField, onApplyClick }: SingleCustomFieldFilterProps) => {
  const {
    id: customFieldId,
    type: { title: customFieldTypeName },
  } = customField;

  const { data } = useGetPrivateCustomFieldOptions({
    customFieldId,
    enabled: selectableCustomFields.includes(customFieldTypeName),
  });

  const hasValues = useAirSelector((store) => hasCustomFieldFilterSelectedSelector(store, customFieldId));

  const dispatch = useDispatch();

  const clearValues = useCallback(() => {
    dispatch(clearCustomFieldFilterValuesAction({ customFieldId }));
  }, [customFieldId, dispatch]);

  const selectedLogic = useAirSelector((state) => customFieldFilterLogicSelector(state, customFieldId));
  const selectedValues = useAirSelector((state) => customFieldFilterValuesSelector(state, customFieldId));

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => dispatch(setCustomFieldFilterLogicAction({ customFieldId, logic })),
    [dispatch, customFieldId],
  );

  const onSelectedValuesChanged = useCallback(
    (values: CustomFieldValueFilterBase[]) => {
      dispatch(setCustomFieldFilterValuesAction({ customFieldId, values }));
      onApplyClick();
    },
    [dispatch, customFieldId, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="customField"
      currentCustomField={customField}
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <CustomFieldFilter
        values={data?.data ?? []}
        onLogicChange={onLogicChange}
        selectedLogic={selectedLogic}
        selectedValues={selectedValues}
        onSelectedValuesChange={onSelectedValuesChanged}
      />
    </SingleFilterContainer>
  );
});

SingleSelectCustomFieldFilter.displayName = 'SingleSelectCustomFieldFilter';

export const SingleCustomFieldFilter = memo((props: SingleCustomFieldFilterProps) => {
  if (props.customField.type.title === CustomFieldTypeName.plainText) {
    return <SinglePlainTextCustomFieldFilter {...props} />;
  }
  if (props.customField.type.title === CustomFieldTypeName.date) {
    return <SingleDateCustomFieldFilter {...props} />;
  }

  return <SingleSelectCustomFieldFilter {...props} />;
});

SingleCustomFieldFilter.displayName = 'SingleCustomFieldFilter';
